import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, ArrowRight, ArrowLeft } from "lucide-react";

interface TourStep {
  target: string;
  title: string;
  description: string;
  position: "top" | "bottom" | "left" | "right";
}

interface OnboardingTourProps {
  onComplete: () => void;
  onSkip: () => void;
  onOptOut: () => void;
}

export const OnboardingTour = ({ onComplete, onSkip, onOptOut }: OnboardingTourProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const tourSteps: TourStep[] = [
    {
      target: "[data-tour='search-form']",
      title: "Suche starten",
      description: "Geben Sie Ihr Reiseziel ein und wählen Sie Ihre Reisedaten. Unsere Suche findet die besten Unterkünfte für Sie.",
      position: "bottom"
    },
    {
      target: "[data-tour='filters']",
      title: "Filter verwenden", 
      description: "Nutzen Sie die Filter um Ihre Suche zu verfeinern - nach Preis, Ausstattung oder Bewertungen.",
      position: "bottom"
    },
    {
      target: "[data-tour='listings']",
      title: "Unterkünfte entdecken",
      description: "Durchstöbern Sie unsere handverlesenen Unterkünfte mit detaillierten Informationen und echten Bewertungen.",
      position: "top"
    },
    {
      target: "[data-tour='user-menu']",
      title: "Ihr Profil",
      description: "Hier verwalten Sie Ihre Buchungen, Einstellungen und können sich als Gastgeber registrieren.",
      position: "bottom"
    }
  ];

  const currentTourStep = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;

  const getTargetElement = () => {
    const element = document.querySelector(currentTourStep.target);
    if (!element) return null;
    
    const rect = element.getBoundingClientRect();
    return {
      element,
      rect: {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      }
    };
  };

  const target = getTargetElement();

  const getTooltipPosition = () => {
    if (!target) return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };

    const { rect } = target;
    const { position } = currentTourStep;

    switch (position) {
      case "top":
        return {
          top: rect.top - 10,
          left: rect.left + rect.width / 2,
          transform: "translate(-50%, -100%)"
        };
      case "bottom":
        return {
          top: rect.top + rect.height + 10,
          left: rect.left + rect.width / 2,
          transform: "translate(-50%, 0)"
        };
      case "left":
        return {
          top: rect.top + rect.height / 2,
          left: rect.left - 10,
          transform: "translate(-100%, -50%)"
        };
      case "right":
        return {
          top: rect.top + rect.height / 2,
          left: rect.left + rect.width + 10,
          transform: "translate(0, -50%)"
        };
      default:
        return {
          top: rect.top + rect.height + 10,
          left: rect.left + rect.width / 2,
          transform: "translate(-50%, 0)"
        };
    }
  };

  const tooltipStyle = getTooltipPosition();

  const handleNext = () => {
    if (isLastStep) {
      if (dontShowAgain) {
        onOptOut();
      } else {
        onComplete();
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    if (dontShowAgain) {
      onOptOut();
    } else {
      onSkip();
    }
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-40" />
      
      {/* Highlight */}
      {target && (
        <div
          className="fixed z-50 rounded-lg ring-4 ring-primary/50 ring-offset-4 ring-offset-background/50 pointer-events-none animate-pulse"
          style={{
            top: target.rect.top - 8,
            left: target.rect.left - 8,
            width: target.rect.width + 16,
            height: target.rect.height + 16
          }}
        />
      )}

      {/* Tooltip */}
      <Card 
        className="fixed z-50 w-80 max-w-[90vw] shadow-strong animate-scale-in"
        style={tooltipStyle}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-semibold text-lg">{currentTourStep.title}</h3>
            <Button variant="ghost" size="sm" onClick={handleSkip}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            {currentTourStep.description}
          </p>

          {/* Checkbox for "Don't show again" - only show on last step */}
          {isLastStep && (
            <div className="flex items-center space-x-2 mb-4">
              <input 
                type="checkbox" 
                id="dont-show-again-tour" 
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="h-4 w-4 text-primary"
              />
              <label 
                htmlFor="dont-show-again-tour" 
                className="text-sm text-muted-foreground cursor-pointer"
              >
                Tutorial nicht mehr anzeigen
              </label>
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">
              {currentStep + 1} von {tourSteps.length}
            </span>
            
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handlePrevious}
                disabled={currentStep === 0}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              
              <Button size="sm" onClick={handleNext}>
                {isLastStep ? (dontShowAgain ? "Nicht mehr anzeigen" : "Fertig") : "Weiter"}
                {!isLastStep && <ArrowRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};